import { Injectable } from '@angular/core';
import * as CryptoJS from 'crypto-js';

//environment
import { environment } from 'src/environments/environment';
import { Location } from 'src/app/models/common-models';

@Injectable({
  providedIn: 'root'
})
export class LocalStorageService {

  private secretKey = environment.localStorage.encryptionKey;
  private encryptionOptions = { mode: CryptoJS.mode.ECB, keySize: 256 / 32, format: CryptoJS.format.OpenSSL };
  private isEncryptionEnabled = environment.localStorage.isEncryptionEnabled;

  private encrypt(data: any): string {
    if (this.isEncryptionEnabled) {
      return CryptoJS.AES.encrypt(JSON.stringify(data), this.secretKey, this.encryptionOptions).toString();
    } else {
      return JSON.stringify(data);
    }
  }

  private decrypt(data: string): any {
    if (this.isEncryptionEnabled) {
      return JSON.parse(CryptoJS.AES.decrypt(data, this.secretKey, this.encryptionOptions).toString(CryptoJS.enc.Utf8));
    } else {
      return JSON.parse(data);
    }
  }

  getProp(category: string, name: string) {
    let propsString = window.localStorage.getItem(category);
    let props = propsString ? this.decrypt(propsString) : {};
    return props[name];
  }

  getProperty<T>(category: string, name: string): T {
    let propsString = window.localStorage.getItem(category);
    let props = propsString ? this.decrypt(propsString) : {};
    return props[name] as T;
  }

  setProp(category: string, name: string, value: any) {
    let propsString = window.localStorage.getItem(category);
    let props = propsString ? this.decrypt(propsString) : {};
    props[name] = value;
    window.localStorage.setItem(category, this.encrypt(props));
  }

  deleteProp(category: string, name: string) {
    let propsString = window.localStorage.getItem(category);
    let props = propsString ? this.decrypt(propsString) : {};

    if (props.hasOwnProperty(name)) {
      delete props[name];
      window.localStorage.setItem(category, this.encrypt(props));
    }
  }

  clearAll() {
    window.localStorage.clear();
  }

  setLocation(location: Location): void {
    this.setProp('location', 'selectedLocation', location);
  }

  getLocation(): Location | null {
    const location = this.getProperty<Location>('location', 'selectedLocation');
    if (!location) {
      return {
        Id: 1,
        Guid: '00000000-0000-0000-0000-000000000000',
        CreatedAt: new Date('0001-01-01 00:00:00.000000'),
        CreatedBy: '00000000-0000-0000-0000-000000000000',
        FacilityId: 0,
        Name: '',
        Description: '',
        AddressLine1: '',
        AddressLine2: '',
        City: '',
        State: '',
        ZipCode: '',
        Country: '',
        Phone: '',
        Email: '',
        Website: '',
        Latitude: 0,
        Longitude: 0,
        TimeZone: '',
        TaxRegionId: 0,
        Order: 0,
        AverageRating: 0,
        ReviewCount: 0,
        PaymentAccountId: '',
        PaymentLocationId: '',
        OpeningDate: '',
        LocationHours: [],
        LocationImages: []
      } as Location;
    }
    return location;
  }

  removeLocation(): void {
    this.deleteProp('location', 'selectedLocation');
  }

  setTempLocation(location: Location): void {
    this.setProp('location', 'selectedTempLocation', location);
  }

  getTempLocation(): Location | null {
    const location = this.getProperty<Location>('location', 'selectedTempLocation');
    if (!location) {
      return null;
    }
    return location;
  }

  removeTempLocation(): void {
    this.deleteProp('location', 'selectedTempLocation');
  }

  // Favorites management methods
  // Category can be: 'class', 'studio', 'instructor', 'trainer'
  // MemberId: 0 for guest users, actual memberId for logged-in users

  private getFavoritesKey(memberId: number): string {
    return `favorites_${memberId}`;
  }

  addFavorite(memberId: number, category: string, itemId: number): void {
    const favoritesKey = this.getFavoritesKey(memberId);
    const favorites = this.getFavoritesByCategory(memberId, category);
    
    if (!favorites.includes(itemId)) {
      favorites.push(itemId);
      this.setProp(favoritesKey, category, favorites);
    }
  }

  removeFavorite(memberId: number, category: string, itemId: number): void {
    const favoritesKey = this.getFavoritesKey(memberId);
    const favorites = this.getFavoritesByCategory(memberId, category);
    const updatedFavorites = favorites.filter(id => id !== itemId);
    this.setProp(favoritesKey, category, updatedFavorites);
  }

  isFavorite(memberId: number, category: string, itemId: number): boolean {
    const favorites = this.getFavoritesByCategory(memberId, category);
    return favorites.includes(itemId);
  }

  getFavoritesByCategory(memberId: number, category: string): number[] {
    const favoritesKey = this.getFavoritesKey(memberId);
    const favorites = this.getProperty<number[]>(favoritesKey, category);
    return favorites || [];
  }

  getAllFavorites(memberId: number): { [category: string]: number[] } {
    const favoritesKey = this.getFavoritesKey(memberId);
    const propsString = window.localStorage.getItem(favoritesKey);
    if (propsString) {
      const props = propsString ? this.decrypt(propsString) : {};
      return props || {};
    }
    return {};
  }

  toggleFavorite(memberId: number, category: string, itemId: number): boolean {
    // Returns true if added, false if removed
    if (this.isFavorite(memberId, category, itemId)) {
      this.removeFavorite(memberId, category, itemId);
      return false;
    } else {
      this.addFavorite(memberId, category, itemId);
      return true;
    }
  }
}